// Temporarily disabled Sentry and FullStory - commented out for easy rollback
// import * as FullStory from '@fullstory/browser';
// import * as Sentry from '@sentry/browser';
// import SentryFullStory from '@sentry/fullstory';
import startCase from 'lodash/startCase';
import posthog from 'posthog-js';
import { useState, useEffect } from 'react';
import { environment, ENVIRONMENTS } from '../../constants';

const { production, staging } = ENVIRONMENTS;

const initChargebee = () => {
  // load subscription management library
  if ((window as any).Chargebee) {
    (window as any).Chargebee.init({
      site:
        environment === production
          ? 'sparrowmarketing'
          : 'sparrowmarketing-test',
      publishableKey:
        environment === production
          ? 'live_DgqfGXq18QzCgfNty1E206AAtZ19LMGn'
          : 'test_ec4n2HUv520NUST0KZXrqcuxPGEEnJW8A',
    });
  }
};

export const initGoogleKeys = () => {
  const googleKeys = {
    clientId:
      environment === production
        ? '573833686643-jl6lr3k3f4trj8a01p85921dubo2dlom.apps.googleusercontent.com'
        : '389027340383-c8podcpe6bs0m5lcr6e5u3hh0birpt8t.apps.googleusercontent.com',
    developerKey:
      environment === production
        ? 'AIzaSyDe9obuyTrA54gcj6O_TH2qCT1qs01erGQ'
        : 'AIzaSyD_blOaHLNKo4Qwg4q5HA5SGvG6wI5A1Bc',
    scope: ['https://www.googleapis.com/auth/drive.file'],
    mimeType: 'application/vnd.google-apps.folder',
    viewId: 'FOLDERS',
  };
  return googleKeys;
};

// Temporarily disabled FullStory initialization
// const initFullStory = () => {
//   // init fullstory
//   FullStory.init({
//     orgId: 'R183M',
//     devMode: environment !== production,
//   });
// };

// Temporarily disabled Sentry initialization
// const initSentry = () => {
//   Sentry.init({
//     dsn:
//       'https://661f68d9e0f74609b3789208059a002a@o348165.ingest.sentry.io/5265272',
//     enabled: environment === production || environment === staging,
//     environment, // this will filter events to staging,production,etc buckets,
//     integrations: [new SentryFullStory('sparrow-marketing')],
//   });
// };

const initSegment = () => {
  window.analytics?.load?.(
    environment === production
      ? 'Plwr4aN3DifJK9XxGJT8t9h4I6RxbaDY'
      : 'f6IGn40FEwLuaX3oTjkzhKcG1ObppnI4'
  );
};

const initPosthog = () => {
  posthog.init(
    environment === 'production'
      ? 'phc_ezD1mcRiqsqrON2WWG2MFvpYG3ECquIMDEeb8gxxDP'
      : 'phc_fAY3VyveTtN0t6D6rcqLW5JUJk9ugmcVOgV23fFZHXD',
    {
      api_host: 'https://mtbmb.viewmetrics.com',
      person_profiles: 'always',
      mask_all_text: false,
      session_recording: {
        maskAllInputs: false,
      },
    }
  );
};

/**
 * React hook for conditional Tolt affiliate tracking
 * Only loads the script when affiliate parameters are present in URL
 */
const useToltAffiliate = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldLoad =
      (urlParams.has('via') || urlParams.has('ref') || urlParams.has('aff')) &&
      !urlParams.get('gclid');

    if (shouldLoad && !isLoaded) {
      const script = document.createElement('script');
      script.src = 'https://cdn.tolt.io/tolt.js';
      script.setAttribute('async', '');
      script.setAttribute('data-tolt', '8796c8aa-747c-43a4-84bc-203a974901ce');

      script.onload = () => setIsLoaded(true);
      script.onerror = () => console.warn('Failed to load Tolt script');

      document.head.appendChild(script);
      setIsLoaded(true);
    }
  }, [isLoaded]);

  return { isLoaded };
};

const isValidUrl = (url: string) => {
  if (!url) return false;
  try {
    /* eslint-disable no-new */
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * This function checks the env and determines which
 * third-party libraries should be initialized and what their init credentials
 * are based on the env.
 *
 * current libraries:
 * - fullstory
 * - chargebee
 * - sentry.io
 * - segment
 * - posthog
 *
 * @returns void: all libraries are initalized on window
 */
const initializeExternalTools = (): void => {
  initChargebee();
  // initFullStory(); // Temporarily disabled
  // initSentry(); // Temporarily disabled
  initSegment();
  initGoogleKeys();
  initPosthog();
};

/**
 * This function shares the identity of our
 * logged-in user with our third party tools
 * are based on the env.
 *
 * current libraries:
 * - fullstory
 * - sentry.io
 * - segment
 *
 * @returns void
 */
const setIdentity = ({ email, id, name, avatar, intercomHmac }: any): void => {
  // Temporarily disabled Sentry user tracking
  // Sentry.configureScope(scope => {
  //   scope.setUser({ email, id });
  // });

  // Temporarily disabled FullStory user tracking
  // FullStory.setUserVars({
  //   displayName: name,
  //   email,
  //   user_id: id,
  // });

  posthog.identify(id, {
    name,
    email,
  });

  if (environment === production) {
    try {
      window.analytics.identify(id, {
        name,
        email,
      });
      window.$crisp.push(['set', 'user:email', [email]]);
      window.$crisp.push(['set', 'user:nickname', [name]]);
      if (isValidUrl(avatar)) {
        window.$crisp.push(['set', 'user:avatar', [avatar]]);
      }
      window.$crisp.push(['set', 'user:identifier', [id]]);
    } catch (error) {
      //
    }
  }
};

const trackPage = (pathName: string): void => {
  const pageName = pathName.substring(1).replace('/', ' ');
  try {
    if (window.analytics?.page) {
      window.analytics.page(pageName);
    }
  } catch (error) {
    // Silently handle if analytics is not available
  }
};

// track wrapper to allow us to share events across other platforms (if necessary in future)
const trackEvent = (
  eventName: string,
  properties: any = {},
  options: any = {},
  callback?: () => void
): void => {
  // window.analytics.track(eventName, properties, options, callback);
  posthog.capture(startCase(eventName), properties, options);
};

const onLogout = () => {
  // remove the helpscout chat widget
  window.$crisp.push(['do', 'chat:hide']);
};

/**
 * Get Tolt referral data from window object
 * @returns Tolt referral string or undefined
 */
const getToltReferral = (): string | undefined => {
  return (window as any).tolt_referral;
};

/**
 * Check if Tolt is loaded and available
 * @returns boolean indicating if Tolt is available
 */
const isToltAvailable = (): boolean => {
  return typeof (window as any).tolt_referral !== 'undefined';
};

export {
  initializeExternalTools,
  onLogout,
  setIdentity,
  trackEvent,
  trackPage,
  getToltReferral,
  isToltAvailable,
  useToltAffiliate,
};
